import { useCallback, useMemo } from 'react';
import { type PoiPreferences, usePoiPreferences, useSetPoiPreferences } from '@/src/lib/hooks/neighborhood';
import { POI_SEARCH_CONFIGS, type POIOptionValue } from './types';

export function normalizePoiPreferenceValues(values: Array<string>): Array<POIOptionValue> {
  const seen = new Set<POIOptionValue>();

  for (const value of values) {
    if (!Object.prototype.hasOwnProperty.call(POI_SEARCH_CONFIGS, value) || seen.has(value)) {
      continue;
    }
    seen.add(value);
  }

  return Array.from(seen);
}

export function resolvePoiPreferenceValues(preferences: PoiPreferences): Array<POIOptionValue> {
  if (!preferences.hasCustomSelection) {
    return [];
  }

  return normalizePoiPreferenceValues(preferences.enabledCategories);
}

export function buildPoiPreferences(values: Array<POIOptionValue>): PoiPreferences {
  return {
    enabledCategories: normalizePoiPreferenceValues(values),
    hasCustomSelection: true,
  };
}

export function usePersistentPoiSelection() {
  const preferences = usePoiPreferences();
  const setPreferences = useSetPoiPreferences();
  const selectedValues = useMemo(() => resolvePoiPreferenceValues(preferences), [preferences]);
  const setSelectedValues = useCallback(
    (values: Array<POIOptionValue>) => setPreferences(buildPoiPreferences(values)),
    [setPreferences],
  );

  return [selectedValues, setSelectedValues] as const;
}
